import { Dialog } from "radix-ui";
import Button from "@/components/bricks/button/Button";
import ArrowLeftIcon from "../../components/icons/ArrowLeftIcon";
import CheckIcon from "../../components/icons/CheckIcon";
import PersonIcon from "../../components/icons/PersonIcon";
import {
  DropdownContent,
  DropdownItem,
  DropdownItemIcon,
  DropdownRoot,
  DropdownSeparator,
  DropdownTrigger,
} from "../../components/dropdown/Dropdown";
import {
  TabsContent,
  TabsList,
  TabsRoot,
  TabsTrigger,
} from "../../components/tabs/Tabs";
import { useContext, useState } from "react";
import Tilsyn from "../../components/pages/person/id/Tilsyn";
import Kursus from "../../components/pages/person/id/Kursus";
import Efteruddannelse from "../../components/pages/person/id/Efteruddannelse";
import Stamoplysninger from "../../components/pages/person/id/Stamoplysninger/Stamoplysninger";
import Sidebar from "../../components/pages/person/id/Sidebar";
import { useNavigate, useParams } from "react-router-dom";
import { useApolloClient, useQuery } from "@apollo/client";
import {
  PERSON_BY_ID_QUERY,
  PERSON_IDKORT_QUERY,
} from "../../api/person/queries";
import PageTitle from "@/components/PageTitle";
import MerePerson from "../../components/pages/person/id/MerePerson";
import Disciplinaert from "../../components/pages/person/id/Disciplinaert";
import SkiftPersonartIcon from "../../components/icons/SkiftPersonartIcon";
import Modal, {
  ModalProvider,
  ModalTrigger,
} from "../../components/Modal/Modal";
import DeponerPersonIcon from "../../components/icons/DeponerPersonIcon";
import DeclareDeadIcon from "../../components/icons/DeclareDeadIcon";
import { PagedPersonQueryContext } from "../../context/PagedPersonQueryProvider";
import EmailIcon from "../../components/icons/EmailIcon";
import { IDPagination } from "../../components/pagination/IDPagination";
import LockIcon from "../../components/icons/LockIcon";
import FlytfirmaIcon from "../../components/icons/FlytfirmaIcon";
import MergepersonIcon from "../../components/icons/MergepersonIcon";
// import GDPRlockWizard from "../../components/wizards/GDPRlockWizard";
import BeskikketAdvokatWizard from "../../components/wizards/BeskikketAdvokat/BeskikketAdvokatWizard";
import {
  extractCurrentPeronBeskikkelse,
  extractCurrentPersonArt,
  extractPersonIdKortHistorik,
} from "../../util/person";
import SendEmailModal from "../../components/sendEmail/SendEmailModal";
import DeponerAdvokatWizard from "../../components/wizards/DeponerAdvokat/DeponerAdvokatWizard";
import BeskikDeponerAdvokatWizard from "../../components/wizards/BeskikDeponerAdvokat/BeskikDeponerAdvokatWizard";
import GenudleverAdvokatWizard from "../../components/wizards/GenudleverAdvokat/GenudleverAdvokatWizard";
import FuldmaegtigAutorisationWizard from "../../components/wizards/FuldmaegtigAutorisation/FuldmaegtigAutorisationWizard";
import RegistrerDoedsfaldWizard from "../../components/wizards/RegistrerDoedsfald/RegistrerDoedsfaldWizard";
import { ErrorBoundary } from "react-error-boundary";
import ErrorFallback from "../../components/ErrorFallback";
import { PersonDto, PersonIdKortStatus } from "../../interfaces/schema";
import usePersonChecks from "../../hooks/errorHandling/usePersonChecks";
import { dateToyyyyMMdd } from "../../util/date";
import { UserQueryContext } from "../../context/UserContext";
import AccessDenied from "../../components/AccessDenied";
import { useReports } from "../../hooks/useQueries";
import ReportIcon from "../../components/icons/ReportIcon";
import { transformQueryDataPerioder } from "../../util/apollo";
import SyncIcon from "../../components/icons/SyncIcon";
import { useConfirm } from "../../components/confirm/useConfirm";
import { post } from "../../api/util";
import { StickyFooter } from "../../components/stickyFooter/modalStickyFooter";
import NytJobWizard from "@/components/wizards/NytJobWizard/NytJobWizard";
import { ReportLink } from "@/components/ReportLink";
import DocumentIcon from "@/components/icons/DocumentIcon";

const PersonById = () => {
  const { user, hasPermission } = useContext(UserQueryContext);
  const canWrite =
    hasPermission(user, "BasisWrite") || hasPermission(user, "KursusWrite");
  const [clear, setClear] = useState(false);
  const params = useParams();
  const navigate = useNavigate();
  const { id } = params;
  const pagedPersonQueryContext = useContext(PagedPersonQueryContext);

  const [activeModal, setActiveModal] =
    useState<ActiveModalTypes>("genudlever");
  const [toggleCpr, setToggleCpr] = useState(false);
  // ADV-349: et vist CPR-nummer må ikke følge med, når man blader til næste person.
  const [cprTogglePersonId, setCprTogglePersonId] = useState(id);
  if (cprTogglePersonId !== id) {
    setCprTogglePersonId(id);
    setToggleCpr(false);
  }

  const { reports: allReports } = useReports();
  const reports = allReports?.filter((r) => r.uiPath === "aika/web/person");
  reports?.sort((a, b) => a.title.localeCompare(b.title));

  const { confirmDialog } = useConfirm();

  const apolloClient = useApolloClient();
  const [data, setData] = useState<
    | {
        person: PersonDto;
      }
    | undefined
  >(undefined);
  const { loading, error, refetch, networkStatus } = useQuery<{
    person: PersonDto;
  }>(
    PERSON_BY_ID_QUERY("personByIdMain", {
      personFields: true,
      personFirma: true,
      personBeskikkelse: true,
      personPrincipalPersonFuldm: true,
      personPrincipalPersonPrincipalNavigation: true,
      personInteresse: true,
      personPersontype: true,
      personUdenlandskadvokatsamfund: true,
      personPersonart: true,
      personIdKort: true,
    }),
    {
      notifyOnNetworkStatusChange: true,
      variables: { id, dato: dateToyyyyMMdd(new Date()) },
      onCompleted: (d) => {
        const transformed = transformQueryDataPerioder(d);
        setData(transformed);
      },
    },
  );
  /**
   * Henter kun ID-kort-bestillingerne igen og lægger dem ind i den viste person.
   *
   * Bevidst ikke refetchPagedQueries()/refetch(): personforespørgslen kører med
   * notifyOnNetworkStatusChange, så et refetch sætter loading — og usePersonChecks erstatter
   * hele personsiden med en spinner, hvorefter fanen remounter og henter sit eget datasæt
   * forfra. Ekspederingen af et ID-kort ændrer én række; den skal ikke koste en sideindlæsning.
   *
   * Værdierne kommer fra serveren frem for at blive gættet lokalt, fordi ekspederingstidspunkt
   * og ekspederetAf sættes af backenden.
   */
  const genhentIdKort = async () => {
    const { data: friskeIdKort } = await apolloClient.query<{
      person: PersonDto;
    }>({
      query: PERSON_IDKORT_QUERY,
      variables: { id },
      fetchPolicy: "network-only",
    });

    setData((current) =>
      current
        ? {
            person: {
              ...current.person,
              personIdKort: friskeIdKort.person.personIdKort,
            },
          }
        : current,
    );
  };

  const refetchPagedQueries = () => {
    if (pagedPersonQueryContext.prevPage) {
      pagedPersonQueryContext.prevPage.refetch();
    }
    if (pagedPersonQueryContext.nextPage) {
      pagedPersonQueryContext.nextPage.refetch();
    }
    if (pagedPersonQueryContext.refetch) pagedPersonQueryContext.refetch();
    refetch();
  };
  const { checkResult, person } = usePersonChecks(
    loading,
    error,
    data,
    networkStatus,
  );
  if (checkResult) return checkResult;
  const { fuldtNavn, foersteBeskikkelse } = person;

  const personart =
    extractCurrentPersonArt(person)?.personartNrNavigation?.navn ?? "";

  const isDeceased = person?.personDoedDato !== null;

  const currentBeskikkelse = extractCurrentPeronBeskikkelse(person);
  const email = person.prioriteretEmail;
  const isREUAdvokat =
    foersteBeskikkelse?.beskikkelsestypeNavigation?.nr ===
    "REGISTRERET_EU_ADVOKAT";
  // Historikken hentes her og gives videre til "Mere om person", fordi det er denne sides
  // tilstand, genhentIdKort() nedenfor opdaterer efter "Markér ID-kort som sendt".
  const idKortHistorik = extractPersonIdKortHistorik(person);
  const personIdKort = idKortHistorik[0];
  const idKortBestilt =
    personIdKort?.status === PersonIdKortStatus.Bestilt
      ? personIdKort
      : undefined;

  const maaIkkeBeskikke =
    !canWrite || personart === "Advokat" || isREUAdvokat || isDeceased;
  const maaIkkeStraksdeponere =
    !canWrite || personart === "Advokat" || isDeceased;
  const maaIkkeDeponereEllerFrakende =
    !canWrite ||
    personart !== "Advokat" ||
    ["FRAKENDT"].indexOf(
      currentBeskikkelse?.beskikkelsestypeNavigation?.nr ?? "",
    ) > -1 ||
    isDeceased;
  const maaIkkeGiveForsteFuldmagtigautorisation =
    !canWrite || personart !== "Øvrig" || isDeceased;
  const maaIkkeGenudlevere =
    !canWrite ||
    ["Deponeret", "Frakendt", "Afregistreret EU-advokat"].indexOf(
      currentBeskikkelse?.beskikkelsestypeNavigation?.navn ?? "",
    ) === -1 ||
    isDeceased;
  const maaIkkeOpdaterBC = !(
    hasPermission(user, "AdminWrite") || hasPermission(user, "KursusWrite")
  );

  const maaIkkeOpraetteNytJob = !canWrite || isDeceased;

  const maaIkkeMarkereIdKortSomSendt = !canWrite || !idKortBestilt;

  /**
   * Både handlingsmenuens punkt og rækkehandlingen i ID-kort-tabellen under "Mere om person"
   * kalder herind (ADV-236), så bekræftelse, ekspedering og genhentning kun findes ét sted.
   * Bestillingen kommer med som argument frem for at være låst til den aktuelle: en person kan
   * have flere åbne bestillinger, siden det unikke indeks blev droppet, og kun tabellen kan pege
   * på de ældre af dem. Menuen sender fortsat den aktuelle med.
   */
  const handleMarkerIdKortSomSendt = async (idKort: { id: string }) => {
    const confirm = await confirmDialog(
      "Markér ID-kort som sendt",
      "Vil du markere ID-kort bestillingen som sendt?",
      "Annuller",
      "Markér som sendt",
    );

    if (!confirm) {
      return;
    }

    try {
      await post({
        query: `person/${person.id}/idkort/${idKort.id}/setbehandlet`,
      });
      await genhentIdKort();
    } catch {
      await confirmDialog(
        "Noget gik galt",
        "ID-kort bestillingen kunne ikke markeres som sendt. Prøv igen.",
        "Luk",
        "OK",
      );
    }
  };

  return (
    <div className="l-main__content">
      <PageTitle title={fuldtNavn} />
      <ErrorBoundary
        FallbackComponent={ErrorFallback}
        onReset={() => navigate("/person")}
      >
        <header className="navbar navbar--sub">
          <div className="col d-flex align-items-center gap-3">
            <Button
              icon
              color="secondary"
              aria-label="Tilbage"
              onClick={() => navigate("/person")}
            >
              <ArrowLeftIcon />
            </Button>
            <h2 className="mx-1 d-flex align-items-center gap-2">
              <span className="d-flex align-items-center">
                <PersonIcon height={26} />
              </span>
              {fuldtNavn}
            </h2>
          </div>
          <div className="col d-flex align-items-center justify-content-end gap-5">
            <ModalProvider>
              <DropdownRoot className="c-dropdown-default--actions">
                <DropdownTrigger>
                  <span className="badge badge--fixed-size ms-n2">
                    <span className="ms-n2">
                      <CheckIcon />
                    </span>
                    <span>Aktuel person</span>
                  </span>
                  <span className="fw-semibold">Handlinger</span>
                </DropdownTrigger>
                <DropdownContent>
                  <DropdownItem asChild>
                    <ModalTrigger
                      disabled={email ? false : true}
                      onClick={() => {
                        setClear(true);
                        setActiveModal("sendemail");
                      }}
                    >
                      <DropdownItemIcon>
                        <EmailIcon />
                      </DropdownItemIcon>
                      <span>Send e-mail ...</span>
                    </ModalTrigger>
                  </DropdownItem>

                  <DropdownSeparator />
                  <DropdownItem asChild>
                    <button
                      id="behandle-id-kort-bestilling"
                      type="button"
                      disabled={maaIkkeMarkereIdKortSomSendt}
                      onClick={() =>
                        idKortBestilt &&
                        handleMarkerIdKortSomSendt(idKortBestilt)
                      }
                    >
                      <DropdownItemIcon>
                        <DocumentIcon />
                      </DropdownItemIcon>
                      <span>Markér ID-kort som sendt ...</span>
                    </button>
                  </DropdownItem>
                  <DropdownItem asChild>
                    <ModalTrigger
                      disabled={maaIkkeBeskikke}
                      onClick={() => setActiveModal("beskikadvokat")}
                    >
                      <DropdownItemIcon>
                        <SkiftPersonartIcon />
                      </DropdownItemIcon>
                      <span>Beskik advokat ...</span>
                    </ModalTrigger>
                  </DropdownItem>
                  <DropdownItem asChild>
                    <ModalTrigger
                      onClick={() => setActiveModal("beskikdeponeradvokat")}
                      disabled={maaIkkeStraksdeponere}
                    >
                      <DropdownItemIcon>
                        <SkiftPersonartIcon />
                      </DropdownItemIcon>
                      <span>Beskik og deponér advokat ...</span>
                    </ModalTrigger>
                  </DropdownItem>
                  <DropdownItem asChild>
                    <ModalTrigger
                      onClick={() => setActiveModal("deponer")}
                      disabled={maaIkkeDeponereEllerFrakende}
                    >
                      <DropdownItemIcon>
                        <DeponerPersonIcon />
                      </DropdownItemIcon>
                      <span>Deponér eller frakend advokat ...</span>
                    </ModalTrigger>
                  </DropdownItem>
                  <DropdownItem asChild>
                    <ModalTrigger
                      disabled={maaIkkeGiveForsteFuldmagtigautorisation}
                      onClick={() => setActiveModal("fuldmaegtigautorisation")}
                    >
                      <DropdownItemIcon>
                        <SkiftPersonartIcon />
                      </DropdownItemIcon>
                      <span>Første fuldmægtigautorisation ...</span>
                    </ModalTrigger>
                  </DropdownItem>
                  <DropdownItem asChild>
                    <ModalTrigger
                      onClick={() => setActiveModal("genudlever")}
                      disabled={maaIkkeGenudlevere}
                    >
                      <DropdownItemIcon>
                        <SkiftPersonartIcon />
                      </DropdownItemIcon>
                      <span>Genudlevér advokat ...</span>
                    </ModalTrigger>
                  </DropdownItem>
                  <DropdownSeparator />
                  <DropdownItem asChild>
                    <ModalTrigger
                      onClick={() => setActiveModal("gdpr-lock")}
                      disabled={!canWrite}
                    >
                      <DropdownItemIcon>
                        <LockIcon />
                      </DropdownItemIcon>
                      <span>GDPR-lås ...</span>
                    </ModalTrigger>
                  </DropdownItem>
                  <DropdownItem asChild>
                    <ModalTrigger
                      onClick={() => setActiveModal("mergeperson")}
                      disabled={!canWrite}
                    >
                      <DropdownItemIcon>
                        <MergepersonIcon />
                      </DropdownItemIcon>
                      <span>Inkorporér anden person ...</span>
                    </ModalTrigger>
                  </DropdownItem>
                  <DropdownItem asChild>
                    <ModalTrigger
                      onClick={() => setActiveModal("nytfirma")}
                      disabled={maaIkkeOpraetteNytJob}
                    >
                      <DropdownItemIcon>
                        <FlytfirmaIcon />
                      </DropdownItemIcon>
                      <span>Nyt job ...</span>
                    </ModalTrigger>
                  </DropdownItem>
                  <DropdownItem asChild>
                    <ModalTrigger
                      onClick={() => setActiveModal("doederklaer")}
                      disabled={!canWrite || isDeceased}
                    >
                      <DropdownItemIcon>
                        <DeclareDeadIcon height={18} />
                      </DropdownItemIcon>
                      <span>Registrér dødsfald ...</span>
                    </ModalTrigger>
                  </DropdownItem>

                  <DropdownItem
                    asChild
                    onClick={async () => {
                      const confirm = await confirmDialog(
                        "Opdater BC",
                        `Her kan du som en éngangshandling overføre opdaterede debitoroplysninger til Business Central for den viste person. 
                        Vær opmærksom på, at debitoropdateringen i BC kører tre gange i døgnet.`,
                        "Annuller",
                        "OK",
                      );

                      if (!confirm) {
                        return;
                      }

                      try {
                        const kundeNr = person.kundeNr;
                        post({
                          query: `syncdebitor/person/${kundeNr}`,
                        }).then(
                          () => {},
                          async () => {
                            await confirmDialog(
                              "Noget gik galt ..",
                              `Noget gik galt, da vi forsøgte at synkronisere med BC. 
                              Kontakt Systemadministrator, hvis problemet fortsætter.`,
                              "Luk",
                              "OK",
                            );
                          },
                        );
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                      } catch (error) {
                        // swallow exception
                      }
                    }}
                    disabled={maaIkkeOpdaterBC}
                  >
                    <div>
                      <DropdownItemIcon>
                        <SyncIcon />
                      </DropdownItemIcon>
                      <span>Opdater BC ...</span>
                    </div>
                  </DropdownItem>

                  {reports && (
                    <>
                      <DropdownSeparator />
                      {reports &&
                        reports.map((r) => {
                          return (
                            <DropdownItem asChild key={r.id}>
                              <ReportLink
                                url={`${r.url}?id=${person.id}`}
                                className="c-dropdown-default-item "
                              >
                                <DropdownItemIcon>
                                  <ReportIcon />
                                </DropdownItemIcon>
                                <span>{r.title}</span>
                              </ReportLink>
                            </DropdownItem>
                          );
                        })}
                    </>
                  )}
                </DropdownContent>
              </DropdownRoot>

              <IDPagination
                pagedContext={pagedPersonQueryContext}
                currentSelection={person}
              />

              {activeModal === "sendemail" && (
                <SendEmailModal
                  type="person"
                  contextData={{
                    selectedIds: [person.id],
                    email: email ?? undefined,
                    totalCount: email ? 1 : 0,
                  }}
                  clear={clear}
                  setClear={setClear}
                />
              )}
              {activeModal === "genudlever" && (
                <Modal
                  title={
                    <>
                      <SkiftPersonartIcon height={22} /> Genudlevér advokat
                    </>
                  }
                  description=""
                >
                  <GenudleverAdvokatWizard
                    person={person}
                    invalidateData={refetchPagedQueries}
                  />
                </Modal>
              )}
              {activeModal === "beskikadvokat" && (
                <Modal
                  title={
                    <>
                      <SkiftPersonartIcon height={22} /> Beskik advokat
                    </>
                  }
                  description=""
                >
                  <BeskikketAdvokatWizard
                    person={person}
                    invalidateData={refetchPagedQueries}
                  />
                </Modal>
              )}
              {activeModal === "beskikdeponeradvokat" && (
                <Modal
                  title={
                    <>
                      <SkiftPersonartIcon height={22} /> Beskik og deponér
                      advokat
                    </>
                  }
                  description=""
                >
                  <BeskikDeponerAdvokatWizard
                    person={person}
                    invalidateData={refetchPagedQueries}
                  />
                </Modal>
              )}
              {activeModal === "fuldmaegtigautorisation" && (
                <Modal
                  title={
                    <>
                      <SkiftPersonartIcon height={22} /> Første
                      fuldmægtigautorisation
                    </>
                  }
                  description=""
                >
                  <FuldmaegtigAutorisationWizard
                    person={person}
                    refetch={refetch}
                  />
                </Modal>
              )}
              {activeModal === "deponer" && (
                <Modal
                  title={
                    <>
                      <DeponerPersonIcon height={22} /> Deponér eller frakend
                      advokat
                    </>
                  }
                  description=""
                >
                  <DeponerAdvokatWizard
                    person={person}
                    invalidateData={refetchPagedQueries}
                  />
                </Modal>
              )}
              {activeModal === "gdpr-lock" && (
                <Modal
                  title={
                    <>
                      <LockIcon height={19} /> GDPR-lås
                    </>
                  }
                  description=""
                >
                  <WizardTodo />
                  {/* <GDPRlockWizard /> */}
                </Modal>
              )}
              {activeModal === "mergeperson" && (
                <Modal
                  title={
                    <>
                      <MergepersonIcon height={20} /> Inkorporér anden person
                    </>
                  }
                  description=""
                >
                  <div className="c-modal-container">
                    <div className="c-item-list c-modal-container-inner">
                      Kontakt Systemadministrator for at få bistand til at
                      sammenlægge data for to personer, som er samme person.
                    </div>
                  </div>
                  <StickyFooter>
                    <Dialog.Close asChild>
                      <Button color="primary" type="button" onClick={() => {}}>
                        Ok
                      </Button>
                    </Dialog.Close>
                  </StickyFooter>
                </Modal>
              )}
              {activeModal === "nytfirma" && (
                <NytJobWizard
                  person={person}
                  invalidateData={refetchPagedQueries}
                />
              )}
              {activeModal === "doederklaer" && (
                <Modal
                  title={
                    <>
                      <DeclareDeadIcon height={22} /> Registrér dødsfald
                    </>
                  }
                  description=""
                >
                  <RegistrerDoedsfaldWizard
                    person={person}
                    invalidateData={refetch}
                  />
                </Modal>
              )}
            </ModalProvider>
          </div>
        </header>
        <div className="l-main-row">
          <Sidebar person={person} />
          <main className="col overflow-hidden">
            <TabsRoot
              defaultValue="stamoplysninger"
              localStorageKey="person-id-tabs"
            >
              <TabsList>
                <div className="container">
                  <TabsTrigger
                    value="stamoplysninger"
                    isDisabled={!hasPermission(user, "BasisRead")}
                  >
                    Stamoplysninger
                  </TabsTrigger>
                  <TabsTrigger
                    value="tilsyn"
                    isDisabled={!hasPermission(user, "TilsynRead")}
                  >
                    Tilsyn
                  </TabsTrigger>
                  <TabsTrigger
                    value="kursus"
                    isDisabled={!hasPermission(user, "KursusRead")}
                  >
                    Kursus
                  </TabsTrigger>
                  <TabsTrigger
                    value="mereomperson"
                    isDisabled={!hasPermission(user, "BasisRead")}
                  >
                    Mere om person
                  </TabsTrigger>
                  <TabsTrigger
                    value="disciplinaert"
                    isDisabled={!hasPermission(user, "DisciplinaertRead")}
                  >
                    Disciplinært
                  </TabsTrigger>
                  <TabsTrigger
                    value="efteruddannelse"
                    isDisabled={!hasPermission(user, "TilsynRead")}
                  >
                    Efteruddannelse
                  </TabsTrigger>
                </div>
              </TabsList>
              <TabsContent value="stamoplysninger">
                {hasPermission(user, "BasisRead") ? (
                  <Stamoplysninger
                    // ADV-349: ny instans pr. person, så redigeringstilstanden ikke følger med
                    // ved bladring (en CprRead-bruger ville ellers se et tømt CPR-felt).
                    key={person.id}
                    person={person}
                    refetch={refetch}
                    toggleCpr={toggleCpr}
                    setToggleCpr={setToggleCpr}
                  />
                ) : (
                  <AccessDenied />
                )}
              </TabsContent>
              <TabsContent value="tilsyn">
                {hasPermission(user, "TilsynRead") ? (
                  <Tilsyn personId={person.id} />
                ) : (
                  <AccessDenied />
                )}
              </TabsContent>
              <TabsContent value="kursus">
                {hasPermission(user, "KursusRead") ? (
                  <Kursus personId={person.id} />
                ) : (
                  <AccessDenied />
                )}
              </TabsContent>
              <TabsContent value="mereomperson">
                {hasPermission(user, "BasisRead") ? (
                  <MerePerson
                    personId={person.id}
                    idKortHistorik={idKortHistorik}
                    markerIdKortSomSendt={
                      canWrite ? handleMarkerIdKortSomSendt : undefined
                    }
                  />
                ) : (
                  <AccessDenied />
                )}
              </TabsContent>
              <TabsContent value="disciplinaert">
                {hasPermission(user, "DisciplinaertRead") ? (
                  <Disciplinaert personId={person.id} />
                ) : (
                  <AccessDenied />
                )}
              </TabsContent>
              <TabsContent value="efteruddannelse">
                {hasPermission(user, "TilsynRead") ? (
                  <Efteruddannelse personId={person.id} />
                ) : (
                  <AccessDenied />
                )}
              </TabsContent>
            </TabsRoot>
          </main>
        </div>
      </ErrorBoundary>
    </div>
  );
};

export default PersonById;

function WizardTodo() {
  return (
    <>
      <div className="c-modal-container">
        <div className="c-item-list c-modal-container-inner">
          Denne wizard er ikke færdigudviklet.
        </div>
      </div>
      <StickyFooter>
        <Dialog.Close asChild>
          <Button color="primary" type="button" onClick={() => {}}>
            Ok
          </Button>
        </Dialog.Close>
      </StickyFooter>
    </>
  );
}
